// Presentational form fields for the broadcast composer. Shared by both
// `BroadcastComposerForm` (create) and `BroadcastEditForm` (update). All
// state is owned by the parent and threaded through controlled props; this
// component is the single visual surface for "what does an email look like
// before sending."
//
// Fields (top → bottom):
//   1. Subject (plain Input)
//   2. Preheader text (Input, maxLength=200)
//   3. Body (TipTap WYSIWYG editor)
//   4. Reply-To (Input type=email, empty = use default)
//   5. Audience (picker — platform only)
//   6. Preview button (opens BroadcastPreviewIframe)
//
// Scheduling lives in the parent's action row via `ScheduleSendButton`.

import { useState } from 'react';
import type { components } from '@leanin/api-client';
import {
  Body,
  Button,
  Caption,
  Card,
  Eyebrow,
  Field,
  Input,
  PanelHeader,
  RadioGroup,
  RadioGroupItem,
  Row,
  Stack,
} from '@leanin/ui';

import { BroadcastAudiencePicker } from '@/components/admin/BroadcastAudiencePicker';
import { usePreviewBroadcast } from '@/lib/mutations/admin';
import { usePreviewGroupBroadcast } from '@/lib/mutations/groups';
import type { BroadcastDoc, BroadcastPreview } from '@/lib/queries/broadcasts';
import { BroadcastBodyEditor } from './BroadcastBodyEditor';
import { BroadcastPreviewIframe } from './BroadcastPreviewIframe';

// ──────────────────────────────────────────────────────────────────────────
// Public types
// ──────────────────────────────────────────────────────────────────────────

export type BroadcastAudienceContext =
  | { readonly mode: 'platform' }
  | {
      readonly mode: 'group';
      readonly kind: 'circle' | 'network' | 'space';
      readonly groupId: string;
      readonly groupName: string;
    };

export type AudienceKind =
  | 'all'
  | 'circles'
  | 'networks'
  | 'members'
  | 'recent_active';

// Canonical API contract — the server's Zod schema, surfaced through
// openapi-types. Both create and update mutations expect this exact shape.
export type AudienceSpec = components['schemas']['AudienceSpec'];

export interface AudienceState {
  readonly kind: AudienceKind;
  readonly circles: ReadonlyArray<{ id: string; name: string; kind: 'circle' }>;
  readonly networks: ReadonlyArray<{ id: string; name: string; kind: 'network' }>;
  readonly members: ReadonlyArray<{ id: string; displayName: string; email: string }>;
  readonly recentDays: number;
}

export interface BroadcastEditorFieldsProps {
  readonly audience: BroadcastAudienceContext;
  readonly subject: string;
  readonly onSubjectChange: (s: string) => void;
  readonly preheaderText: string;
  readonly onPreheaderChange: (s: string) => void;
  readonly bodyDoc: BroadcastDoc;
  readonly onBodyDocChange: (doc: BroadcastDoc) => void;
  readonly audienceState: AudienceState;
  readonly onAudienceStateChange: (s: AudienceState) => void;
  readonly replyTo: string;
  readonly onReplyToChange: (s: string) => void;
  /** Existing broadcast id — required for preview, since the server's
   *  `POST /broadcasts/{id}/preview` is row-derived (in-memory overrides
   *  layered on top of the stored row). For create-mode where the row
   *  doesn't exist yet, the Preview button is hidden. */
  readonly broadcastIdForPreview?: string;
  readonly disabled?: boolean;
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers — audience-spec derivation
// ──────────────────────────────────────────────────────────────────────────

const AUDIENCE_OPTIONS: ReadonlyArray<{
  readonly id: AudienceKind;
  readonly label: string;
  readonly meta: string;
}> = [
  { id: 'all', label: 'All members', meta: 'Everyone in the workspace' },
  { id: 'circles', label: 'Specific circles', meta: 'One or more circles' },
  { id: 'networks', label: 'Specific networks', meta: 'One or more networks' },
  { id: 'members', label: 'Specific members', meta: 'Hand-picked recipients' },
  {
    id: 'recent_active',
    label: 'Recent active',
    meta: 'Members active in the last N days',
  },
];

export interface ResolvedAudience {
  readonly audienceSpec: AudienceSpec;
}

export function resolveAudience(state: AudienceState): ResolvedAudience {
  switch (state.kind) {
    case 'all':
      return { audienceSpec: { kind: 'all' } };
    case 'recent_active':
      return {
        audienceSpec: { kind: 'recent_active', days: state.recentDays },
      };
    case 'circles':
      return {
        audienceSpec: {
          kind: 'circles',
          circleIds: [...state.circles.map((c) => c.id)],
        },
      };
    case 'networks':
      return {
        audienceSpec: {
          kind: 'networks',
          networkIds: [...state.networks.map((n) => n.id)],
        },
      };
    case 'members':
      return {
        audienceSpec: {
          kind: 'members',
          userIds: [...state.members.map((m) => m.id)],
        },
      };
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────

export function BroadcastEditorFields({
  audience,
  subject,
  onSubjectChange,
  preheaderText,
  onPreheaderChange,
  bodyDoc,
  onBodyDocChange,
  audienceState,
  onAudienceStateChange,
  replyTo,
  onReplyToChange,
  broadcastIdForPreview,
  disabled,
}: BroadcastEditorFieldsProps) {
  const [preview, setPreview] = useState<BroadcastPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const platformPreview = usePreviewBroadcast();
  const groupPreview = usePreviewGroupBroadcast();
  const previewPending =
    platformPreview.isPending || groupPreview.isPending;

  const handlePreview = async () => {
    if (broadcastIdForPreview === undefined) return;
    setPreviewError(null);
    try {
      const previewArgs = {
        bodyDoc,
        subject,
        preheaderText: preheaderText.length > 0 ? preheaderText : null,
      } as const;
      const result =
        audience.mode === 'platform'
          ? await platformPreview.mutateAsync({
              id: broadcastIdForPreview,
              ...previewArgs,
            })
          : await groupPreview.mutateAsync({
              kind: audience.kind,
              groupId: audience.groupId,
              broadcastId: broadcastIdForPreview,
              ...previewArgs,
            });
      setPreview(result);
    } catch (err) {
      setPreviewError(
        err instanceof Error ? err.message : 'Failed to generate preview.',
      );
    }
  };

  return (
    <>
      <Stack gap="lg">
        {/* Subject + Preheader */}
        <Card pad="flush">
          <PanelHeader>
            <Eyebrow tone="accent">Subject &amp; preview</Eyebrow>
          </PanelHeader>
          <Stack p="xl" gap="lg">
            <Field label="Subject" required>
              <Input
                name="subject"
                value={subject}
                onChange={(e) => onSubjectChange(e.target.value)}
                maxLength={200}
                placeholder="A short, descriptive subject"
                disabled={disabled}
              />
            </Field>
            <Field
              label="Preview text"
              description="The inbox preview line (~120 characters)."
            >
              <Input
                name="preheaderText"
                value={preheaderText}
                onChange={(e) => onPreheaderChange(e.target.value)}
                maxLength={200}
                placeholder="What members see in their inbox before opening"
                disabled={disabled}
              />
            </Field>
          </Stack>
        </Card>

        {/* Body */}
        <Card pad="flush">
          <PanelHeader>
            <Eyebrow tone="accent">Message</Eyebrow>
          </PanelHeader>
          <Stack p="xl" gap="lg">
            <Field label="Body" required>
              <BroadcastBodyEditor
                value={bodyDoc}
                onChange={onBodyDocChange}
                disabled={disabled}
              />
            </Field>
            <Field
              label="Reply-to (optional)"
              description="Members who reply land in this inbox instead of the default."
            >
              <Input
                type="email"
                name="replyTo"
                value={replyTo}
                onChange={(e) => onReplyToChange(e.target.value)}
                placeholder="hello@leanin.org"
                disabled={disabled}
              />
            </Field>
          </Stack>
        </Card>

        {/* Audience */}
        <Card pad="flush">
          <PanelHeader>
            <Eyebrow tone="accent">Audience</Eyebrow>
          </PanelHeader>
          <Stack p="xl" gap="lg">
            {audience.mode === 'group' ? (
              <Body size="sm" color="mute">
                Sending to all members of{' '}
                <strong>{audience.groupName}</strong>. This cannot be
                changed.
              </Body>
            ) : (
              <PlatformAudiencePicker
                state={audienceState}
                onChange={onAudienceStateChange}
                disabled={disabled === true}
              />
            )}
          </Stack>
        </Card>

        {/* Preview row */}
        {broadcastIdForPreview !== undefined ? (
          <Row gap="md" align="center" justify="flex-start">
            <Button
              type="button"
              variant="secondary"
              size="md"
              disabled={disabled}
              loading={previewPending}
              onClick={() => void handlePreview()}
            >
              Preview email
            </Button>
            {previewError !== null ? (
              <Body size="sm" color="destructive">
                {previewError}
              </Body>
            ) : null}
          </Row>
        ) : null}
      </Stack>

      {preview !== null ? (
        <BroadcastPreviewIframe
          html={preview.html}
          open
          onClose={() => setPreview(null)}
        />
      ) : null}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Platform-mode audience picker (private)
// ──────────────────────────────────────────────────────────────────────────

interface PlatformAudienceProps {
  readonly state: AudienceState;
  readonly onChange: (s: AudienceState) => void;
  readonly disabled: boolean;
}

function PlatformAudiencePicker({
  state,
  onChange,
  disabled,
}: PlatformAudienceProps) {
  return (
    <>
      <RadioGroup
        value={state.kind}
        onValueChange={(v) => onChange({ ...state, kind: v as AudienceKind })}
        aria-label="Audience"
      >
        <Stack gap="sm">
          {AUDIENCE_OPTIONS.map((opt) => (
            <RadioGroupItem key={opt.id} value={opt.id} disabled={disabled}>
              <Stack gap="xs">
                <Body size="md">{opt.label}</Body>
                <Caption>{opt.meta}</Caption>
              </Stack>
            </RadioGroupItem>
          ))}
        </Stack>
      </RadioGroup>

      {state.kind === 'circles' ? (
        <BroadcastAudiencePicker
          kind="circles"
          selectedIds={state.circles.map((c) => c.id)}
          selectedItems={state.circles}
          onChange={(_ids, items) => onChange({ ...state, circles: items })}
        />
      ) : null}

      {state.kind === 'networks' ? (
        <BroadcastAudiencePicker
          kind="networks"
          selectedIds={state.networks.map((n) => n.id)}
          selectedItems={state.networks}
          onChange={(_ids, items) => onChange({ ...state, networks: items })}
        />
      ) : null}

      {state.kind === 'members' ? (
        <BroadcastAudiencePicker
          kind="members"
          selectedIds={state.members.map((m) => m.id)}
          selectedItems={state.members}
          onChange={(_ids, items) => onChange({ ...state, members: items })}
        />
      ) : null}

      {state.kind === 'recent_active' ? (
        <Field label="Active within (days)" required>
          <Input
            type="number"
            min={1}
            max={365}
            value={state.recentDays}
            onChange={(e) =>
              onChange({
                ...state,
                recentDays: Number(e.target.value) || 30,
              })
            }
            disabled={disabled}
          />
        </Field>
      ) : null}
    </>
  );
}
